// Position-based collision separation for design-studio wall items.
//
// Keeps wall items from overlapping by pushing them apart while letting each item
// "gravitate" back toward its own rest position (anchor). Pure + dependency-free so it
// can run inside the FrameRenderer RAF loop (live) and during scene resolution (settle).
//
// Coordinate space: inches, item-center positions, axis-aligned bounding boxes.
//
// Box shape: { key, x, y, halfW, halfH, pinned, anchorX, anchorY }
//   x/y       current center (mutated in place)
//   halfW/halfH  half footprint (already includes any per-item padding the caller wants)
//   pinned    true => held in place (e.g. the item the user is dragging/resizing); only pushes
//   anchorX/Y rest position gravity pulls a free box back toward

// Gap kept between footprints while interactively resolving collisions.
export const WALL_COLLISION_MARGIN_INCHES = 36
// Fraction of the anchor delta a free box moves back per iteration (0..1).
export const WALL_COLLISION_GRAVITY = 0.2
// Fixed iterations per frame in live (drag/resize) mode.
export const WALL_COLLISION_LIVE_ITERATIONS = 4
// Settle (commit) mode: iterate until movement falls below epsilon, capped. The cap is generous
// because settle only runs on commit (not per frame) and a long tightly-packed chain needs many
// Gauss-Seidel passes to fully clear.
export const WALL_COLLISION_SETTLE_MAX_ITERATIONS = 128
export const WALL_COLLISION_SETTLE_EPSILON = 0.01
// Within this vertical gap two items count as "same height", so a vertical escape biases up
// consistently instead of depending on left/right order (which would flip up↔down mid-pass).
export const WALL_COLLISION_SAME_HEIGHT_EPSILON = 0.5

function sign(value) {
  return value < 0 ? -1 : 1
}

// Sweep-and-prune along x: returns candidate overlapping index pairs.
// Sorting by left edge lets us stop scanning a row once a later box starts past this box's right.
function sweepAndPrunePairs(boxes, margin) {
  const order = boxes.map((_, i) => i)
  order.sort((a, b) => (boxes[a].x - boxes[a].halfW) - (boxes[b].x - boxes[b].halfW))

  const pairs = []
  for (let oi = 0; oi < order.length; oi += 1) {
    const i = order[oi]
    const iRight = boxes[i].x + boxes[i].halfW + margin
    for (let oj = oi + 1; oj < order.length; oj += 1) {
      const j = order[oj]
      const jLeft = boxes[j].x - boxes[j].halfW
      if (jLeft >= iRight) break // no later box (sorted) can overlap i on x
      pairs.push([i, j])
    }
  }
  return pairs
}

// Separate one overlapping pair (sign-based MTV), so an item wedged between two neighbors parts
// them outward rather than shoving one side. Returns the magnitude of movement applied.
//
// When the centers are horizontally aligned (one item is "over" the other — i.e. you're dragging
// through its body) we escape VERTICALLY so the neighbor steps up/down to let the mover pass,
// rather than popping to the far side. This can't fall out of plain minimum-penetration: items
// sit at the same eye level, so the full-height vertical overlap always exceeds the horizontal
// one and MTV would always slide sideways. For shallow edge contact we keep the minimal (usually
// horizontal) move, which preserves the wedge-into-a-gap reflow.
function separatePair(a, b, margin) {
  if (a.pinned && b.pinned) return 0

  const dx = b.x - a.x
  const dy = b.y - a.y
  const overlapX = a.halfW + b.halfW + margin - Math.abs(dx)
  const overlapY = a.halfH + b.halfH + margin - Math.abs(dy)
  if (overlapX <= 0 || overlapY <= 0) return 0 // not actually overlapping (incl. margin)

  const deeplyAlignedOnX = Math.abs(dx) < Math.min(a.halfW, b.halfW)
  if (deeplyAlignedOnX || overlapY < overlapX) {
    // Vertical escape. When the pair is at the same height the up/down choice is ambiguous; pick
    // a direction that does NOT depend on left/right ordering (bias the free box up), so a
    // neighbor the dragged item passes through goes consistently up instead of flipping above↔
    // below as the dragged item crosses its center.
    const dirY = Math.abs(dy) > WALL_COLLISION_SAME_HEIGHT_EPSILON
      ? sign(dy)
      : (b.pinned ? -1 : 1) // free box moves +y (up) regardless of which side is a vs b
    return applySeparation(a, b, overlapY, 0, dirY)
  }
  return applySeparation(a, b, overlapX, sign(dx || 1), 0)
}

// Move the pair apart by `pen` along (dirX, dirY): pinned boxes hold; a free box paired with a
// pinned one absorbs the full penetration; two free boxes split it 50/50.
function applySeparation(a, b, pen, dirX, dirY) {
  if (a.pinned) {
    b.x += pen * dirX
    b.y += pen * dirY
    return pen
  }
  if (b.pinned) {
    a.x -= pen * dirX
    a.y -= pen * dirY
    return pen
  }
  const half = pen / 2
  a.x -= half * dirX
  a.y -= half * dirY
  b.x += half * dirX
  b.y += half * dirY
  return pen
}

function applyGravity(box, gravity) {
  if (box.pinned) return 0
  const mx = (box.anchorX - box.x) * gravity
  const my = (box.anchorY - box.y) * gravity
  box.x += mx
  box.y += my
  return Math.abs(mx) + Math.abs(my)
}

/**
 * Resolve overlaps among wall boxes in place.
 *
 * @param {Array} boxes  array of { key, x, y, halfW, halfH, pinned, anchorX, anchorY }
 * @param {Object} [opts]
 * @param {number} [opts.iterations]      fixed iteration count (live mode); omit for settle mode.
 * @param {number} [opts.gravity]
 * @param {number} [opts.margin]
 * @returns {Array} the same boxes (x/y mutated)
 */
export function resolveWallCollisions(boxes, opts = {}) {
  if (!Array.isArray(boxes) || boxes.length < 2) return boxes

  const fixed = Number.isFinite(opts.iterations) ? opts.iterations : null
  const maxIterations = fixed != null ? fixed : WALL_COLLISION_SETTLE_MAX_ITERATIONS

  // Gravity (spring back toward the anchor) is a live-drag behavior so neighbors float home when
  // the dragged item leaves. The settle/commit pass must fully separate, so it defaults gravity
  // off — otherwise gravity and separation balance into an equilibrium that leaves overlaps.
  const isLive = fixed != null
  const gravity = Number.isFinite(opts.gravity)
    ? opts.gravity
    : (isLive ? WALL_COLLISION_GRAVITY : 0)
  const margin = Number.isFinite(opts.margin) ? opts.margin : WALL_COLLISION_MARGIN_INCHES

  for (let iter = 0; iter < maxIterations; iter += 1) {
    let movement = 0
    for (let i = 0; i < boxes.length; i += 1) movement += applyGravity(boxes[i], gravity)

    const pairs = sweepAndPrunePairs(boxes, margin)
    for (let p = 0; p < pairs.length; p += 1) {
      movement += separatePair(boxes[pairs[p][0]], boxes[pairs[p][1]], margin)
    }

    // Settle mode stops once the system is at rest; live mode always runs the fixed count.
    if (fixed == null && movement < WALL_COLLISION_SETTLE_EPSILON) break
  }

  return boxes
}
